import React from "react";
import { DateTimeInput } from "react-admin";
import { zonedTimeToUtc } from "date-fns-tz";

import { formatInYardTimeZone, getIANATimezone } from "../../utils/dateUtils";

// yyyy-MM-ddThh:mm — the wire format of <input type="datetime-local">.
const DATE_TIME_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * Renders a UTC timestamp as the yard's wall clock.
 *
 * Must be idempotent: react-admin's DateTimeInput formats the stored value
 * through useInput and then formats `input.value` a second time on the way to
 * the TextField, so an already-formatted string has to pass through untouched.
 */
export const formatInYard = (value) => {
  // null/undefined/"" must return "" rather than undefined, or the input
  // becomes uncontrolled.
  if (value == null || value === "") return "";
  if (typeof value === "string" && DATE_TIME_LOCAL.test(value)) return value;
  const parsed = value instanceof Date ? value : new Date(value);
  if (isNaN(parsed.getTime())) return "";
  return formatInYardTimeZone(parsed, "yyyy-MM-dd'T'HH:mm");
};

/**
 * Reads the typed/picked wall clock back as the yard's, not the browser's.
 *
 * With no yard zone configured this lands on the browser zone, matching what
 * formatInYard renders through formatInYardTimeZone so the field still round
 * trips; formatInYardTimeZone is what reports the misconfiguration.
 */
export const parseFromYard = (value) => {
  if (!value) return null;
  const timeZone = getIANATimezone();
  if (!timeZone) return new Date(value);
  return zonedTimeToUtc(value, timeZone);
};

/**
 * Replacement for react-admin's <DateTimeInput> for UTC timestamps.
 *
 * <DateTimeInput> converts with local getters (getHours/getDate), so it shows
 * the *viewer's* wall clock: a carrier in Eastern time who picks an 8:00 AM
 * slot at a Pacific yard sees the field read 11:00 AM. Appointments are yard
 * wall clock by definition, so both directions go through the yard's IANA
 * zone instead — the input counterpart to <YardDateField>.
 */
export const YardDateTimeInput = (props) => (
  <DateTimeInput {...props} format={formatInYard} parse={parseFromYard} />
);
